export interface Category {
  id: string;
  slug: string;
  name: string;
  shortDescription: string;
  description: string;
  icon: string;
  productCount: number;
  keyFeatures: string[];
  typicalApplications: string[];
  industries: string[];
}

export const categories: Category[] = [
  {
    id: 'gas-analyzers',
    slug: 'gas-analyzers',
    name: 'Gas Analyzers (Stack & Process)',
    shortDescription: 'Continuous emissions monitoring and process gas analysis for industrial stacks and process streams.',
    description: 'Instrutech supplies a comprehensive range of stack and process gas analyzers for continuous emissions monitoring (CEMS), process optimization, and regulatory compliance. Our portfolio covers extractive and in-situ analyzer technologies suitable for harsh industrial environments.',
    icon: 'BeakerIcon',
    productCount: 4,
    keyFeatures: [
      'CEMS-compliant continuous monitoring',
      'Multi-component gas measurement (SO₂, NOₓ, CO, CO₂, O₂)',
      'Extractive and in-situ analyzer configurations',
      'Hazardous area rated options (ATEX/IECEx)',
      'Integrated sample conditioning systems',
      'Remote diagnostics and data logging',
    ],
    typicalApplications: [
      'Stack emissions monitoring (power plants, cement, steel)',
      'Furnace and boiler optimization',
      'Process gas quality control (refineries)',
      'Flare gas monitoring',
      'Combustion efficiency optimization',
    ],
    industries: ['Power Plants', 'Oil & Gas', 'Refineries', 'Cement/Metals', 'Environmental Monitoring'],
  },
  {
    id: 'control-valves',
    slug: 'control-valves',
    name: 'Control Valves / Desuperheaters',
    shortDescription: 'Precision flow and temperature control valves for steam, gas, and liquid process applications.',
    description: 'Our control valve and desuperheater range covers globe, butterfly, ball, and rotary designs for demanding process conditions. We supply actuated assemblies with positioners, limit switches, and solenoids for complete loop integration.',
    icon: 'AdjustmentsHorizontalIcon',
    productCount: 4,
    keyFeatures: [
      'Globe, butterfly, ball, and rotary configurations',
      'Pneumatic, electric, and hydraulic actuators',
      'ANSI/ISA and IEC 60534 compliant sizing',
      'High pressure and high temperature variants',
      'Digital positioners with HART/PROFIBUS',
      'Desuperheater spray nozzle assemblies',
    ],
    typicalApplications: [
      'Steam temperature and pressure control',
      'Cooling water flow regulation',
      'Feedwater control in boiler systems',
      'Process fluid throttling (refineries)',
      'Turbine bypass and let-down stations',
    ],
    industries: ['Power Plants', 'Refineries', 'Oil & Gas', 'Cement/Metals', 'EPC/OEM'],
  },
  {
    id: 'vibration-monitoring',
    slug: 'vibration-monitoring',
    name: 'Vibration Monitoring Systems',
    shortDescription: 'Online and portable vibration monitoring for rotating machinery protection and predictive maintenance.',
    description: 'Continuous and portable vibration monitoring systems for turbines, compressors, pumps, and motors. Our range includes proximity probes, velocity transducers, accelerometers, and complete rack-based monitoring systems per API 670.',
    icon: 'ChartBarIcon',
    productCount: 3,
    keyFeatures: [
      'API 670 compliant machinery protection',
      'Proximity probes (eddy current) for shaft monitoring',
      'Seismic velocity and accelerometer sensors',
      'Rack-based and standalone monitors',
      'Alarm and trip relay outputs',
      'Integration with DCS/SCADA systems',
    ],
    typicalApplications: [
      'Gas turbine and steam turbine protection',
      'Centrifugal compressor monitoring',
      'Pump and motor bearing health',
      'Gearbox vibration analysis',
      'Fan and blower condition monitoring',
    ],
    industries: ['Oil & Gas', 'Power Plants', 'Refineries', 'Cement/Metals'],
  },
  {
    id: 'ambient-air-quality',
    slug: 'ambient-air-quality',
    name: 'Ambient Air Quality Monitoring Systems',
    shortDescription: 'Regulatory-grade ambient air monitoring stations for industrial fence-line and community monitoring.',
    description: 'Complete ambient air quality monitoring systems (AAQMS) for industrial fence-line monitoring, community health compliance, and environmental impact assessment. Systems measure PM2.5, PM10, SO₂, NOₓ, O₃, CO, VOCs, and meteorological parameters.',
    icon: 'CloudIcon',
    productCount: 3,
    keyFeatures: [
      'PM2.5, PM10, SO₂, NOₓ, O₃, CO measurement',
      'CPCB/EPA reference method compliance',
      'Integrated meteorological station option',
      'Remote data transmission (GPRS/LAN)',
      'Weatherproof enclosure with HVAC',
      'Automated QA/QC and calibration',
    ],
    typicalApplications: [
      'Industrial fence-line monitoring',
      'Urban ambient air quality networks',
      'Environmental impact assessment',
      'Mine site dust monitoring',
      'Construction project compliance',
    ],
    industries: ['Environmental Monitoring', 'Oil & Gas', 'Cement/Metals', 'EPC/OEM'],
  },
  {
    id: 'water-quality',
    slug: 'water-quality',
    name: 'Water Quality Analysis Panels',
    shortDescription: 'Online water chemistry analysis panels for cooling water, boiler water, and effluent monitoring.',
    description: 'Modular online water quality analysis panels for continuous monitoring of cooling tower water, boiler feedwater, condensate, and industrial effluent. Parameters include pH, conductivity, dissolved oxygen, turbidity, chlorine, silica, and phosphate.',
    icon: 'EyeDropperIcon',
    productCount: 3,
    keyFeatures: [
      'Multi-parameter online analysis',
      'pH, conductivity, DO, turbidity, chlorine',
      'Silica, phosphate, hardness analyzers',
      'Automatic sample conditioning',
      'Modular panel-mounted design',
      '4-20mA and MODBUS outputs',
    ],
    typicalApplications: [
      'Cooling tower chemistry control',
      'Boiler feedwater monitoring',
      'Condensate quality monitoring',
      'Effluent treatment compliance',
      'Reverse osmosis system monitoring',
    ],
    industries: ['Power Plants', 'Refineries', 'Oil & Gas', 'Cement/Metals'],
  },
  {
    id: 'explosion-proof',
    slug: 'explosion-proof',
    name: 'Explosion Proof Electrical',
    shortDescription: 'ATEX/IECEx certified explosion-proof and flameproof electrical equipment for hazardous areas.',
    description: 'Comprehensive range of explosion-proof (Ex d), intrinsically safe (Ex i), and increased safety (Ex e) electrical equipment for Zone 1, Zone 2, and Zone 0 hazardous areas. Includes junction boxes, control stations, luminaires, motors, and distribution boards.',
    icon: 'BoltIcon',
    productCount: 3,
    keyFeatures: [
      'ATEX and IECEx certified',
      'Zone 0, 1, 2 and Division 1, 2 ratings',
      'Ex d (flameproof) and Ex i (intrinsically safe)',
      'GRP and stainless steel enclosures',
      'Certified for gas groups IIA, IIB, IIC',
      'IP66/IP67 ingress protection',
    ],
    typicalApplications: [
      'Offshore platform electrical distribution',
      'Refinery process area wiring',
      'Gas compression station controls',
      'Chemical plant area classification',
      'Fuel storage and dispensing areas',
    ],
    industries: ['Oil & Gas', 'Refineries', 'EPC/OEM', 'Cement/Metals'],
  },
  {
    id: 'sample-handling',
    slug: 'sample-handling',
    name: 'Sample Handling Components',
    shortDescription: 'Sample conditioning systems, probes, and transport lines for reliable process gas and liquid analysis.',
    description: 'Sample handling and conditioning systems ensure representative, clean, and dry samples reach your analyzers. Our range includes sample probes, heated sample lines, conditioning units, filters, coolers, and complete sample systems per ISA-77.44.',
    icon: 'FunnelIcon',
    productCount: 3,
    keyFeatures: [
      'Heated and unheated sample lines',
      'Probe designs for stack and process',
      'Moisture separators and coalescing filters',
      'Calibration gas injection provisions',
      'Corrosion-resistant wetted materials',
      'Modular cabinet-mounted systems',
    ],
    typicalApplications: [
      'CEMS sample extraction and conditioning',
      'Process analyzer sample systems',
      'Flue gas moisture removal',
      'Particulate filtration for gas analyzers',
      'Calibration gas management',
    ],
    industries: ['Power Plants', 'Refineries', 'Oil & Gas', 'Environmental Monitoring', 'Cement/Metals'],
  },
  {
    id: 'sensors-gauges',
    slug: 'sensors-gauges',
    name: 'Industrial Sensors & Gauges',
    shortDescription: 'Pressure, temperature, level, and flow instruments for general and critical process measurement.',
    description: 'A broad range of process measurement instruments including pressure transmitters, temperature sensors (RTD/thermocouple), level gauges, flow meters, and differential pressure instruments. Supplied for general industrial and safety-critical applications.',
    icon: 'CpuChipIcon',
    productCount: 4,
    keyFeatures: [
      'Pressure transmitters (gauge, absolute, differential)',
      'RTD and thermocouple assemblies',
      'Magnetic level gauges and transmitters',
      'Vortex, magnetic, and Coriolis flow meters',
      'HART, PROFIBUS, and WirelessHART protocols',
      'SIL-rated options for safety applications',
    ],
    typicalApplications: [
      'Process pressure and temperature measurement',
      'Tank level monitoring and gauging',
      'Flow measurement in pipelines',
      'Custody transfer metering',
      'Safety instrumented system (SIS) loops',
    ],
    industries: ['Oil & Gas', 'Refineries', 'Power Plants', 'Cement/Metals', 'EPC/OEM'],
  },
];

export function getCategoryBySlug(slug: string): Category | undefined {
  return categories.find(c => c.slug === slug);
}